// src/components/pages/Foam/FoamOverviewCard.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Skeleton,
} from "@mui/material";
import { StatusIndicator } from "../../common/StatusIndicator"; // adjust path if needed
import FoamWinderStatus from "../../common/FoamWinderStatus";
import FoamPressureAlarms from "../../common/FoamPressureAlarms";
import { format } from "date-fns";
import { api } from "../../../api/client"; // <-- your axios instance with interceptors

// mirrors your foam shape (subset we need)
type FoamReading = {
  ts_utc: string;
  plc: string;
  butane_control: boolean;
  sv?: number | null;
  pv?: number | null;
  low_pressure?: number | null;
  high_pressure_1?: number | null;
  high_pressure_2?: number | null;
  hp_warning_setpoint?: number | null;
  hp_shutdown_setpoint?: number | null;
  hp_warning_active?: boolean | null;
  hp_shutdown_active?: boolean | null;
  age_seconds?: number;
};

type BlenderStatus = "running" | "idle" | "stopped" | "";

const FoamOverviewCard: React.FC = () => {
  const [latest, setLatest] = useState<FoamReading | null>(null);
  const [foamBlenderStatus, setFoamBlenderStatus] = useState<BlenderStatus>("");
  // First load only — the 60s poll refreshes in place without skeletons.
  // Until then every value is unknown, not "—" or "stopped".
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const fetchFoamLatest = async () => {
    try {
      // axios instance baseURL already set via interceptors/config
      const { data } = await api.get<FoamReading[]>("/foam/latest");
      const rows = Array.isArray(data) ? data : [];
      const newest =
        rows
          .slice()
          .sort(
            (a, b) =>
              new Date(b.ts_utc).getTime() - new Date(a.ts_utc).getTime()
          )[0] ?? null;
      if (mountedRef.current) setLatest(newest);
    } catch {
      // errors (including 401) are handled by the interceptor; no-op here
    }
  };

  // blender_id=5 for foam
  const fetchFoamBlenderStatus = async () => {
    try {
      const { data } = await api.get<Record<string, { status: BlenderStatus }>>(
        "/blender/currentStatus"
      );
      const s = data?.["5"]?.status ?? "";
      if (mountedRef.current) setFoamBlenderStatus(s);
    } catch {
      // interceptor handles auth/refresh; keep UI stable
    }
  };

  const poll = async () => {
    await Promise.all([fetchFoamLatest(), fetchFoamBlenderStatus()]);
    if (mountedRef.current) setLoading(false);
  };

  useEffect(() => {
    mountedRef.current = true;
    // initial load
    poll();
    // poll every 60s
    const id = setInterval(poll, 60_000);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, []);

  const num = (n: number | null | undefined, d = 1) =>
    n == null ? "—" : Number(n).toFixed(d);

  const updatedAt = useMemo(
    () => (latest ? format(new Date(latest.ts_utc), "PP p") : "—"),
    [latest]
  );

  const kpis = [
    { label: "SV", value: num(latest?.sv) },
    { label: "PV", value: num(latest?.pv) },
    { label: "High P1", value: num(latest?.high_pressure_1, 0) },
    {
      // Same signal the foam page charts as "Water Temperature":
      // high_pressure_2 carries the water temp in °C.
      label: "Water Temp",
      value:
        latest?.high_pressure_2 == null
          ? "—"
          : `${num(latest.high_pressure_2, 1)} °C`,
    },
  ];

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flex: 1, p: { xs: 2, md: 2.5 } }}>
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          flexWrap="wrap"
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Foam Line
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={150} />
          ) : (
            <Typography variant="caption" color="text.secondary">
              Updated {updatedAt}
            </Typography>
          )}
        </Stack>

        {/* Status side by side — the old stacked column left the card
            lopsided once it became half-width on the overview. */}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {loading ? (
            // Same footprint as StatusIndicator, so nothing shifts on arrival.
            <>
              <Skeleton variant="rounded" width={180} height={72} />
              <Skeleton variant="rounded" width={180} height={72} />
            </>
          ) : (
            <>
              <StatusIndicator title="Foam Blender" status={foamBlenderStatus} />
              <StatusIndicator
                title="Butane Control"
                status={latest?.butane_control ? "running" : "stopped"}
              />
            </>
          )}
        </Stack>

        <Box sx={{ mt: 2 }}>
          <FoamPressureAlarms reading={latest} loading={loading} />
        </Box>

        {/* Winder comes from its own PLC/worker, so it carries its own
            freshness rather than riding on the foam reading above. */}
        <Box sx={{ mt: 2 }}>
          <FoamWinderStatus />
        </Box>

        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "block", mt: 2 }}
        >
          Latest snapshot
        </Typography>

        {/* noWrap keeps every label on one line, so the values stay on a
            common baseline (\"Water Temp\" used to wrap and drop its number). */}
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(4, minmax(0, 1fr))",
            },
            mt: 0.5,
          }}
        >
          {kpis.map((k) => (
            <Box
              key={k.label}
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: "block" }}
              >
                {k.label}
              </Typography>
              <Typography variant="h6" noWrap>
                {loading ? <Skeleton variant="text" width="60%" /> : k.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default FoamOverviewCard;
